/**
 * Image Lightbox & Info Dialog Component
 *
 * Full-screen image lightbox modal and image info dialog
 * showing generation settings and prompt details.
 */

import { useEffect, useCallback } from 'react';
import { Download, ChevronDown, Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { GeneratedImage, ENHANCEMENT_CATEGORIES } from '../types/image-generator.types';

export interface ImageLightboxProps {
  lightboxImage: GeneratedImage | null;
  infoImage: GeneratedImage | null;
  onCloseLightbox: () => void;
  onCloseInfo: () => void;
  onDownload: (image: GeneratedImage) => void;
  getModelDisplayName: (modelName: string) => string;
}

export function ImageLightbox({
  lightboxImage,
  infoImage,
  onCloseLightbox,
  onCloseInfo,
  onDownload,
  getModelDisplayName,
}: ImageLightboxProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxImage) {
        onCloseLightbox();
      }
    },
    [lightboxImage, onCloseLightbox],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      {/* Image Info Dialog */}
      <Dialog open={!!infoImage} onOpenChange={(open) => !open && onCloseInfo()}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Image Settings</DialogTitle>
          </DialogHeader>
          {infoImage && (
            <div className="space-y-4">
              {/* Model info */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Model</h4>
                <p className="text-sm">{getModelDisplayName(infoImage.model)}</p>
              </div>

              {/* Original prompt */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Prompt</h4>
                <p className="text-sm whitespace-pre-wrap">{infoImage.prompt}</p>
              </div>

              {/* DALL-E 3 Revisions */}
              {infoImage.revisedPrompt && (
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-1 group">
                    <h4 className="text-sm font-medium text-muted-foreground">DALL-E 3 Revision</h4>
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    <div className="relative ml-1">
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help peer" />
                      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-80 p-4 bg-popover border border-border rounded-lg shadow-xl text-xs text-muted-foreground opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-[100]">
                        DALL-E 3 automatically enhances your prompt to generate more detailed and
                        accurate images. The AI expands on your original description, adding
                        artistic details, composition guidance, and stylistic elements to better
                        capture your intent. This revised prompt shows exactly what DALL-E 3 used to
                        create your image. This is a built-in feature of DALL-E 3 and cannot be
                        turned off directly. For more control, edit the fields in the Image Settings
                        Tab.
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="text-sm">
                      <span className="font-medium">Revised Prompt:</span>{' '}
                      <span className="text-muted-foreground">{infoImage.revisedPrompt}</span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Settings */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Settings</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-muted/50 rounded px-2 py-1">
                    <span className="text-muted-foreground">Size:</span> {infoImage.size}
                  </div>
                  <div className="bg-muted/50 rounded px-2 py-1">
                    <span className="text-muted-foreground">Quality:</span> {infoImage.quality}
                  </div>
                  <div className="bg-muted/50 rounded px-2 py-1">
                    <span className="text-muted-foreground">Style:</span> {infoImage.style}
                  </div>
                </div>
              </div>

              {/* Reference image info */}
              {infoImage.usedReferenceImage && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Reference Image
                  </h4>
                  <div className="text-sm bg-muted/50 rounded px-2 py-1">
                    Used a reference image with{' '}
                    <span className="font-medium">
                      {infoImage.creativityPercent ?? 50}% creativity
                    </span>
                  </div>
                </div>
              )}

              {/* Enhancements used */}
              {Object.entries(infoImage.enhancements).some(([, value]) => value?.trim()) && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Prompt Enhancements
                  </h4>
                  <div className="space-y-2">
                    {ENHANCEMENT_CATEGORIES.filter((cat) =>
                      infoImage.enhancements[cat.key]?.trim(),
                    ).map((cat) => (
                      <div key={cat.key} className="text-sm">
                        <span className="font-medium">{cat.label}:</span>{' '}
                        <span className="text-muted-foreground">
                          {infoImage.enhancements[cat.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated at timestamp */}
              <div className="text-xs text-muted-foreground pt-2 border-t">
                Generated: {new Date(infoImage.generatedAt).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={onCloseLightbox}
        >
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white/70 hover:text-white hover:bg-white/10"
            onClick={onCloseLightbox}
          >
            <X className="h-6 w-6" />
          </Button>

          <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImage.imageUrl}
              alt={lightboxImage.prompt}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 flex gap-3">
              <Button
                variant="secondary"
                onClick={() => onDownload(lightboxImage)}
                className="gap-2 bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                <Download className="h-4 w-4" />
                Save Image
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
